import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  LogOut, Upload, Trash2, Image as ImageIcon, Calendar, 
  MessageCircle, Users, X, Star, CheckCircle, Send, 
  AlertCircle, TrendingUp, ExternalLink, ArrowRight, UserPlus,
  FileText, DollarSign, Bot, Settings, Clock, BarChart3,
  Phone, Bell, Zap, ToggleLeft, ToggleRight, Filter,
  Ban, Edit, Check, Globe, Link2, ThumbsUp, ThumbsDown,
  Briefcase, HardHat, CalendarDays, Eye, Search, Archive,
  Plus, Mail, Copy, XCircle, CreditCard
} from "lucide-react";
import JobsTab from "./admin/JobsTab";
import EmployeesTab from "./admin/EmployeesTab";
import FinancialTab from "./admin/FinancialTab";
import ScheduleTab from "./admin/ScheduleTab";
import ClientProfileModal from "./admin/ClientProfileModal";
import GhostAdMakerTab from "./admin/GhostAdMakerTab";
import RemindersTab from "./admin/RemindersTab";
import FinancingLeadsTab from "./admin/FinancingLeadsTab";

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState("jobs");
  const [galleryImages, setGalleryImages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [ghostLeads, setGhostLeads] = useState([]);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [internalFeedback, setInternalFeedback] = useState([]);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [ghostStats, setGhostStats] = useState(null);
  const [ghostSettings, setGhostSettings] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [ghostSessions, setGhostSessions] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [publicReviews, setPublicReviews] = useState([]);
  const [leadFilter, setLeadFilter] = useState("all");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [editingGallery, setEditingGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [newReview, setNewReview] = useState({ name: "", rating: 5, message: "", service: "", approved: true });
  
  // Upload state
  const [uploadData, setUploadData] = useState({ title: "", description: "", category: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Client modal
  const [showClientModal, setShowClientModal] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "", address: "", password: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [galleryRes, appointmentsRes, contactsRes, leadsRes, reviewsRes, statsRes, feedbackRes, clientsRes, quotesRes, ghostStatsRes, ghostSettingsRes, followUpsRes, sessionsRes, employeesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/appointments`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { withCredentials: true }),
        axios.get(`${API}/ghost-leads`, { withCredentials: true }),
        axios.get(`${API}/reviews/requests`, { withCredentials: true }),
        axios.get(`${API}/reviews/stats`, { withCredentials: true }),
        axios.get(`${API}/reviews/internal-feedback`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/quotes`, { withCredentials: true }),
        axios.get(`${API}/ghost-stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/ghost-settings`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/follow-ups/due`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/ghost-sessions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setGalleryImages(galleryRes.data);
      setAppointments(appointmentsRes.data);
      setContacts(contactsRes.data);
      setGhostLeads(leadsRes.data);
      setReviewRequests(reviewsRes.data);
      setReviewStats(statsRes.data);
      setInternalFeedback(feedbackRes.data);
      setClients(clientsRes.data);
      setQuotes(quotesRes.data);
      setGhostStats(ghostStatsRes.data);
      setGhostSettings(ghostSettingsRes.data);
      setFollowUps(followUpsRes.data);
      setGhostSessions(sessionsRes.data);
      setEmployees(employeesRes.data);
      setInvoices(invoicesRes.data);
      // Load additional data
      try {
        const [siteRes, pubRevRes] = await Promise.all([
          axios.get(`${API}/site-settings`),
          axios.get(`${API}/reviews/all`, { withCredentials: true })
        ]);
        setSiteSettings(siteRes.data);
        setPublicReviews(pubRevRes.data);
      } catch (e) { console.error(e); }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadData.title || !uploadData.description) {
      toast.error("Please select an image, add a title, and provide a description");
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        await axios.post(`${API}/gallery`, {
          title: uploadData.title,
          description: uploadData.description,
          category: uploadData.category,
          image_data: base64Image
        }, { withCredentials: true });

        toast.success("Image uploaded successfully!");
        setUploadData({ title: "", description: "", category: "" });
        setSelectedFile(null);
        setPreviewUrl(null);
        fetchData();
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    
    try {
      await axios.delete(`${API}/gallery/${imageId}`, { withCredentials: true });
      toast.success("Image deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const handleCompleteJob = async (appointmentId) => {
    try {
      const response = await axios.patch(
        `${API}/appointments/${appointmentId}/complete`, 
        {}, 
        { withCredentials: true }
      );
      toast.success("Job completed! Review request created.");
      fetchData();
    } catch (error) {
      toast.error("Failed to complete job");
    }
  };

  const handleSendReviewRequest = async (reviewId) => {
    try {
      await axios.patch(
        `${API}/reviews/request/${reviewId}/send`,
        {},
        { withCredentials: true }
      );
      toast.success("Review request marked as sent");
      fetchData();
    } catch (error) {
      toast.error("Failed to send review request");
    }
  };

  const copyReviewLink = (reviewId) => {
    const link = `${window.location.origin}/review/${reviewId}`;
    navigator.clipboard.writeText(link);
    toast.success("Review link copied to clipboard!");
  };

  // Global Search
  const handleGlobalSearch = async (term) => {
    setGlobalSearchTerm(term);
    if (term.length < 2) { setSearchResults(null); return; }
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(term)}`, { withCredentials: true });
      setSearchResults(res.data);
    } catch (e) { setSearchResults(null); }
  };

  // Client Delete
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Delete this client? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}`, { withCredentials: true });
      toast.success("Client deleted");
      fetchData();
    } catch (e) { toast.error("Failed to delete client"); }
  };

  // Quote Send
  const handleSendQuote = async (quoteId, method) => {
    try {
      const endpoint = method === "sms" ? "send-sms" : "send-email";
      await axios.post(`${API}/quotes/${quoteId}/${endpoint}`, {}, { withCredentials: true });
      toast.success(`Quote sent via ${method}`);
      fetchData();
    } catch (e) { toast.error("Failed to send quote"); }
  };

  const copyQuoteLink = (quoteId) => {
    const link = `${window.location.origin}/quote/${quoteId}`;
    navigator.clipboard.writeText(link);
    toast.success("Quote link copied!");
  };

  // Review management
  const handleApproveReview = async (reviewId) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}/approve`, {}, { withCredentials: true });
      toast.success("Review approved");
      fetchData();
    } catch (e) { toast.error("Failed to approve"); }
  };

  const handleUnapproveReview = async (reviewId) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}/unapprove`, {}, { withCredentials: true });
      toast.success("Review hidden from public");
      fetchData();
    } catch (e) { toast.error("Failed to unapprove"); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Review deleted");
      fetchData();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, { withCredentials: true });
      toast.success("Quote deleted");
      fetchData();
    } catch (e) { toast.error("Failed to delete quote"); }
  };

  const handleCreateReview = async () => {
    if (!newReview.name || !newReview.message) { toast.error("Name and review text required"); return; }
    try {
      await axios.post(`${API}/reviews/create`, newReview, { withCredentials: true });
      toast.success("Review created");
      setShowAddReviewModal(false);
      setNewReview({ name: "", rating: 5, message: "", service: "", approved: true });
      fetchData();
    } catch (e) { toast.error("Failed to create review"); }
  };

  const tabs = [
    { id: "jobs", label: "Work Orders", icon: Briefcase, count: 0 },
    { id: "quotes", label: "Quotes", icon: FileText, count: quotes.length },
    { id: "clients", label: "Customers", icon: UserPlus, count: clients.length },
    { id: "financial", label: "Payments", icon: DollarSign, count: invoices.filter(i => i.status === "unpaid").length },
    { id: "reminders", label: "Reminders", icon: Bell, count: 0 },
    { id: "employees", label: "Team", icon: HardHat, count: employees.length },
    { id: "schedule", label: "Schedule", icon: CalendarDays, count: 0 },
    { id: "ad-maker", label: "Ad Maker", icon: Zap, count: 0 },
    { id: "ghost-ai", label: "Ghost AI", icon: Bot, count: followUps.length },
    { id: "pub-reviews", label: "Reviews", icon: Star, count: publicReviews.length },
    { id: "gallery", label: "Gallery", icon: ImageIcon, count: galleryImages.length },
    { id: "site-settings", label: "Settings", icon: Settings, count: 0 },
    { id: "leads", label: "Leads", icon: MessageCircle, count: ghostLeads.length },
    { id: "financing-leads", label: "Financing", icon: CreditCard, count: 0 },
    { id: "contacts", label: "Contacts", icon: Users, count: contacts.length }
  ];

  const categories = ["Kitchen", "Bathroom", "Living Room", "Exterior", "Flooring", "Painting", "Other"];

  const handleImportToSales = async (ghostLeadId) => {
    try {
      await axios.post(`${API}/leads/import-ghost/${ghostLeadId}`, {}, { withCredentials: true });
      toast.success("Lead imported to Sales CRM!");
    } catch (error) {
      if (error.response?.data?.message?.includes("already imported")) {
        toast.info("Lead already in Sales CRM");
      } else {
        toast.error("Failed to import lead");
      }
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.email || !newClient.phone || !newClient.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await axios.post(`${API}/clients`, newClient, { withCredentials: true });
      toast.success("Client created successfully!");
      setShowClientModal(false);
      setNewClient({ name: "", email: "", phone: "", address: "", password: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    }
  };

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="admin-page">
      {/* Header */}
      <div className="bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-[#A0AAB2] text-sm">Welcome, {user?.name || "Admin"}</p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/crm"
                className="flex items-center space-x-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold"
                data-testid="go-to-crm"
              >
                <TrendingUp size={18} />
                <span>Sales CRM</span>
                <ArrowRight size={16} />
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-[#A0AAB2] hover:text-white hover:border-[#FF6B35] transition-all"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Global Search */}
        <div className="relative mb-4">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
            <input type="text" value={globalSearchTerm} onChange={e => handleGlobalSearch(e.target.value)}
              placeholder="Search clients, jobs, quotes, leads..." data-testid="global-search"
              className="w-full pl-11 pr-4 py-3 bg-[#15161C] border border-[#2A2B32] rounded-xl text-white text-sm placeholder:text-[#A0AAB2]/50 focus:outline-none focus:border-[#FF6B35]" />
            {globalSearchTerm && (
              <button onClick={() => { setGlobalSearchTerm(""); setSearchResults(null); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] hover:text-white">
                <X size={16} />
              </button>
            )}
          </div>
          {searchResults && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-[#15161C] border border-[#2A2B32] rounded-xl p-4 max-h-80 overflow-y-auto shadow-xl" data-testid="search-results">
              {Object.entries(searchResults).every(([, v]) => v.length === 0) ? (
                <p className="text-[#A0AAB2] text-center py-2 text-sm">No results found</p>
              ) : (
                <>
                  {searchResults.clients?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[#FF6B35] text-xs font-bold mb-1 uppercase">Clients</p>
                      {searchResults.clients.map(c => (
                        <button key={c.id} onClick={() => { setSelectedClientId(c.id); setSearchResults(null); setGlobalSearchTerm(""); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1F2027] text-white text-sm">{c.name} — {c.phone}</button>
                      ))}
                    </div>
                  )}
                  {searchResults.jobs?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-blue-400 text-xs font-bold mb-1 uppercase">Jobs</p>
                      {searchResults.jobs.map(j => (
                        <button key={j.id} onClick={() => { setActiveTab("jobs"); setSearchResults(null); setGlobalSearchTerm(""); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1F2027] text-white text-sm flex items-center justify-between" data-testid={`search-job-${j.id}`}>
                          <span>{j.title} — {j.client_name}</span>
                          <span className="text-[#A0AAB2] text-xs">{j.status}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.quotes?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-purple-400 text-xs font-bold mb-1 uppercase">Quotes</p>
                      {searchResults.quotes.map(q => (
                        <button key={q.id} onClick={() => { navigate(`/admin/quotes/${q.id}`); setSearchResults(null); setGlobalSearchTerm(""); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1F2027] text-white text-sm flex items-center justify-between" data-testid={`search-quote-${q.id}`}>
                          <span>{q.quote_number} — {q.title}</span>
                          <span className="text-[#FF6B35] text-xs">${q.total_price?.toLocaleString()}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.leads?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-green-400 text-xs font-bold mb-1 uppercase">Leads</p>
                      {searchResults.leads.map(l => (
                        <button key={l.id} onClick={() => { setActiveTab("leads"); setSearchResults(null); setGlobalSearchTerm(""); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1F2027] text-white text-sm flex items-center justify-between" data-testid={`search-lead-${l.id}`}>
                          <span>{l.name} — {l.phone}</span>
                          <span className="text-[#A0AAB2] text-xs">{l.service}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-[#FF6B35] text-white"
                    : "bg-[#15161C] border border-[#2A2B32] text-[#A0AAB2] hover:text-white"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? "bg-white/20" : "bg-[#2A2B32]"
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: "Create Quote", icon: FileText, action: () => navigate("/admin/quotes/new"), color: "from-[#FF6B35] to-[#FF8B5E]" },
            { label: "View Schedule", icon: CalendarDays, action: () => setActiveTab("schedule"), color: "from-blue-600 to-blue-400" },
            { label: "Add Customer", icon: UserPlus, action: () => { setActiveTab("clients"); setShowClientModal(true); }, color: "from-green-600 to-green-400" },
            { label: "View Payments", icon: DollarSign, action: () => setActiveTab("financial"), color: "from-yellow-600 to-yellow-400" },
            { label: "Ad Maker", icon: Zap, action: () => setActiveTab("ad-maker"), color: "from-purple-600 to-purple-400" }
          ].map((qa, i) => (
            <button key={i} onClick={qa.action} data-testid={`quick-action-${i}`}
              className={`bg-gradient-to-br ${qa.color} rounded-xl p-4 text-left hover:scale-[1.02] transition-transform`}>
              <qa.icon size={20} className="text-white mb-2" />
              <p className="text-white text-sm font-semibold">{qa.label}</p>
            </button>
          ))}
        </div>

        {/* Tab Content */}

        {/* Work Orders Tab */}
        {activeTab === "jobs" && (
          <JobsTab clients={clients} employees={employees} quotes={quotes} onRefresh={fetchData} />
        )}

        {/* Financial Tab */}
        {activeTab === "financial" && (
          <FinancialTab clients={clients} onOpenClient={(id) => setSelectedClientId(id)} />
        )}

        {/* Reminders Tab */}
        {activeTab === "reminders" && (
          <RemindersTab onOpenClient={(id) => setSelectedClientId(id)} />
        )}

        {/* Employees Tab */}
        {activeTab === "employees" && (
          <EmployeesTab onRefresh={fetchData} />
        )}

        {/* Schedule Tab */}
        {activeTab === "schedule" && (
          <ScheduleTab />
        )}

        {/* Clients Tab */}
        {activeTab === "clients" && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Clients ({clients.length})</h2>
              <button
                onClick={() => setShowClientModal(true)}
                className="flex items-center space-x-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold"
                data-testid="add-client-btn"
              >
                <UserPlus size={16} />
                <span>Add Client</span>
              </button>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
              </div>
            ) : clients.length === 0 ? (
              <p className="text-[#A0AAB2] text-center py-8">No clients yet. Add your first client above.</p>
            ) : (
              <div className="space-y-4">
                {clients.map((client) => (
                  <div key={client.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4 cursor-pointer hover:border-[#FF6B35]/50 transition-all"
                    onClick={() => setSelectedClientId(client.id)} data-testid={`client-row-${client.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-white font-semibold">{client.name}</h3>
                        <p className="text-[#A0AAB2] text-sm">{client.email}</p>
                        {client.address && (
                          <p className="text-[#A0AAB2] text-xs">{client.address}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <a
                          href={`tel:${client.phone}`}
                          onClick={e => e.stopPropagation()}
                          className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30"
                        >
                          Call
                        </a>
                        <p className="text-white text-sm">{client.phone}</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedClientId(client.id); }}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-[#FF6B35]/20 text-[#FF6B35] rounded-lg text-sm hover:bg-[#FF6B35]/30"
                          data-testid={`view-client-${client.id}`}
                        >
                          <Eye size={14} /><span>Profile</span>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id); }}
                          className="p-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Quotes ({quotes.length})</h2>
              <Link
                to="/admin/quotes/new"
                className="flex items-center space-x-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold"
                data-testid="create-quote-btn"
              >
                <FileText size={16} />
                <span>New Quote</span>
              </Link>
            </div>

            {quotes.length === 0 ? (
              <p className="text-[#A0AAB2] text-center py-8">No quotes yet. Create your first quote above.</p>
            ) : (
              <div className="space-y-3">
                {quotes.map((quote) => (
                  <div key={quote.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4 hover:border-[#FF6B35]/50 transition-all"
                    data-testid={`quote-row-${quote.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <Link to={`/admin/quotes/${quote.id}`} className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-white font-semibold">{quote.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            quote.status === "draft" ? "bg-gray-500/20 text-gray-400" :
                            quote.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                            quote.status === "viewed" ? "bg-cyan-500/20 text-cyan-400" :
                            quote.status === "approved" ? "bg-green-500/20 text-green-400" :
                            quote.status === "signed" ? "bg-emerald-500/20 text-emerald-400" :
                            quote.status === "invoiced" ? "bg-purple-500/20 text-purple-400" :
                            "bg-red-500/20 text-red-400"
                          }`}>
                            {quote.status}
                          </span>
                          {quote.tracking_status && quote.tracking_status !== quote.status && (
                            <span className="text-[#A0AAB2] text-xs">({quote.tracking_status})</span>
                          )}
                        </div>
                        <p className="text-[#A0AAB2] text-sm">{quote.quote_number} &middot; {quote.client_name}</p>
                        {quote.sent_at && <p className="text-[#A0AAB2] text-xs mt-0.5">Sent {quote.sent_via || ""} on {new Date(quote.sent_at).toLocaleDateString()}</p>}
                        {quote.viewed_at && <p className="text-cyan-400 text-xs">Viewed: {new Date(quote.viewed_at).toLocaleDateString()}</p>}
                      </Link>
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <p className="text-[#FF6B35] font-bold">${quote.total_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                          <p className="text-green-400 text-xs">Profit: ${quote.profit?.toLocaleString(undefined, {minimumFractionDigits: 2})} ({quote.profit_percent}%)</p>
                        </div>
                        {/* Actions */}
                        <div className="flex items-center space-x-1">
                          {quote.status === "draft" && (
                            <>
                              <button onClick={(e) => { e.preventDefault(); handleSendQuote(quote.id, "email"); }}
                                className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30" title="Send via Email"
                                data-testid={`send-email-${quote.id}`}>
                                <Mail size={14} />
                              </button>
                              <button onClick={(e) => { e.preventDefault(); handleSendQuote(quote.id, "sms"); }}
                                className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30" title="Send via SMS"
                                data-testid={`send-sms-${quote.id}`}>
                                <Phone size={14} />
                              </button>
                            </>
                          )}
                          {quote.status !== "draft" && (
                            <button onClick={(e) => { e.preventDefault(); copyQuoteLink(quote.id); }}
                              className="p-2 bg-[#2A2B32] text-[#A0AAB2] rounded-lg hover:text-white" title="Copy Client Link"
                              data-testid={`copy-link-${quote.id}`}>
                              <Copy size={14} />
                            </button>
                          )}
                          {(quote.status === "signed" || quote.status === "approved") && (
                            <button onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const res = await axios.post(`${API}/quotes/${quote.id}/auto-convert`, {}, { withCredentials: true });
                                toast.success("Converted to Work Order + Invoice!");
                                fetchData();
                              } catch (err) { toast.error("Failed to convert"); }
                            }}
                              className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30" title="Convert to Work Order + Invoice"
                              data-testid={`convert-quote-${quote.id}`}>
                              <CheckCircle size={14} />
                            </button>
                          )}
                          <Link to={`/admin/quotes/${quote.id}`} className="p-2 bg-[#2A2B32] text-[#A0AAB2] rounded-lg hover:text-white" data-testid={`edit-quote-${quote.id}`}>
                            <Edit size={14} />
                          </Link>
                          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteQuote(quote.id); }}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30" title="Delete quote"
                            data-testid={`delete-quote-${quote.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Ghost Ad Maker Tab */}
        {activeTab === "ad-maker" && (
          <GhostAdMakerTab />
        )}

        {/* Ghost AI Tab */}
        {activeTab === "ghost-ai" && (
          <div className="space-y-6">
            {/* Performance Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <BarChart3 size={16} className="text-blue-400" />
                  <span className="text-[#A0AAB2] text-xs">Sessions</span>
                </div>
                <p className="text-xl font-bold text-white" data-testid="ghost-total-sessions">{ghostStats?.total_sessions || 0}</p>
                <p className="text-[#A0AAB2] text-xs">{ghostStats?.recent_sessions_7d || 0} this week</p>
              </div>
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <Users size={16} className="text-green-400" />
                  <span className="text-[#A0AAB2] text-xs">Leads Captured</span>
                </div>
                <p className="text-xl font-bold text-green-400" data-testid="ghost-leads-captured">{ghostStats?.leads_captured || 0}</p>
                <p className="text-[#A0AAB2] text-xs">{ghostStats?.recent_leads_7d || 0} this week</p>
              </div>
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <TrendingUp size={16} className="text-[#FF6B35]" />
                  <span className="text-[#A0AAB2] text-xs">Conversion</span>
                </div>
                <p className="text-xl font-bold text-[#FF6B35]" data-testid="ghost-conversion">{ghostStats?.conversion_rate || 0}%</p>
              </div>
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <Zap size={16} className="text-purple-400" />
                  <span className="text-[#A0AAB2] text-xs">CRM Imported</span>
                </div>
                <p className="text-xl font-bold text-purple-400">{ghostStats?.crm_imported || 0}</p>
              </div>
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <Bell size={16} className="text-yellow-400" />
                  <span className="text-[#A0AAB2] text-xs">Pending Follow-ups</span>
                </div>
                <p className="text-xl font-bold text-yellow-400" data-testid="ghost-pending-followups">{ghostStats?.pending_follow_ups || 0}</p>
              </div>
            </div>

            {/* Due Follow-ups */}
            {followUps.length > 0 && (
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
                <h3 className="text-base font-semibold text-white mb-4 flex items-center space-x-2">
                  <Bell size={18} className="text-yellow-400" />
                  <span>Follow-ups Due ({followUps.length})</span>
                </h3>
                <div className="space-y-3">
                  {followUps.map((fu) => (
                    <div key={fu.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-white font-medium">{fu.lead_name || "Unknown"}</span>
                          <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">{fu.delay_label}</span>
                        </div>
                        <p className="text-[#A0AAB2] text-sm">{fu.message}</p>
                        {fu.lead_phone && (
                          <a href={`tel:${fu.lead_phone}`} className="flex items-center space-x-1 text-[#FF6B35] text-sm mt-1 hover:underline">
                            <Phone size={12} /><span>{fu.lead_phone}</span>
                          </a>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            await axios.patch(`${API}/follow-ups/${fu.id}`, { status: "completed" }, { withCredentials: true });
                            toast.success("Marked as completed");
                            fetchData();
                          }}
                          className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs font-semibold hover:bg-green-500/30"
                          data-testid={`complete-followup-${fu.id}`}
                        >
                          Done
                        </button>
                        <button
                          onClick={async () => {
                            await axios.patch(`${API}/follow-ups/${fu.id}`, { status: "dismissed" }, { withCredentials: true });
                            toast.info("Follow-up dismissed");
                            fetchData();
                          }}
                          className="px-3 py-1.5 bg-[#2A2B32] text-[#A0AAB2] rounded-lg text-xs font-semibold hover:text-white"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ghost Settings */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center space-x-2">
                <Settings size={18} className="text-[#A0AAB2]" />
                <span>Ghost Settings</span>
              </h3>
              {ghostSettings && (
                <div className="space-y-4">
                  {[
                    { key: "automation_enabled", label: "Automation Enabled", desc: "Enable automated follow-ups and lead capture" },
                    { key: "follow_up_1h", label: "1-Hour Follow-up", desc: "Send follow-up 1 hour after lead capture" },
                    { key: "follow_up_24h", label: "24-Hour Follow-up", desc: "Send follow-up 24 hours after lead capture" },
                    { key: "follow_up_3d", label: "3-Day Follow-up", desc: "Send follow-up 3 days after lead capture" },
                    { key: "upsell_enabled", label: "Upsell Suggestions", desc: "Ghost suggests related services after estimate" }
                  ].map((setting) => (
                    <div key={setting.key} className="flex items-center justify-between py-3 border-b border-[#2A2B32] last:border-0">
                      <div>
                        <p className="text-white text-sm font-medium">{setting.label}</p>
                        <p className="text-[#A0AAB2] text-xs">{setting.desc}</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newVal = !ghostSettings[setting.key];
                          await axios.patch(`${API}/ghost-settings`, { [setting.key]: newVal }, { withCredentials: true });
                          setGhostSettings(prev => ({ ...prev, [setting.key]: newVal }));
                          toast.success(`${setting.label} ${newVal ? "enabled" : "disabled"}`);
                        }}
                        className="p-1"
                        data-testid={`toggle-${setting.key}`}
                      >
                        {ghostSettings[setting.key] ? (
                          <ToggleRight size={28} className="text-green-400" />
                        ) : (
                          <ToggleLeft size={28} className="text-[#A0AAB2]" />
                        )}
                      </button>
                    </div>
                  ))}

                  {/* Custom Greeting */}
                  <div className="pt-4">
                    <label className="block text-sm text-[#A0AAB2] mb-2">Custom Greeting Message</label>
                    <textarea
                      value={ghostSettings.custom_greeting || ""}
                      onChange={(e) => setGhostSettings(prev => ({ ...prev, custom_greeting: e.target.value }))}
                      rows={2}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm resize-none"
                      data-testid="ghost-greeting-input"
                    />
                    <button
                      onClick={async () => {
                        await axios.patch(`${API}/ghost-settings`, { custom_greeting: ghostSettings.custom_greeting }, { withCredentials: true });
                        toast.success("Greeting updated");
                      }}
                      className="mt-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold"
                      data-testid="save-greeting-btn"
                    >
                      Save Greeting
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Sessions */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h3 className="text-base font-semibold text-white mb-4">Recent Chat Sessions ({ghostSessions.length})</h3>
              {ghostSessions.length === 0 ? (
                <p className="text-[#A0AAB2] text-center py-4">No sessions yet</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {ghostSessions.slice(0, 20).map((session) => (
                    <div key={session.session_id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-white text-sm font-medium">{session.lead_data?.name || "Anonymous"}</span>
                          {session.lead_saved && <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Lead Captured</span>}
                        </div>
                        <span className="text-[#A0AAB2] text-xs">{new Date(session.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-[#A0AAB2] text-xs">{session.messages?.length || 0} messages</p>
                      {session.lead_data?.service && <p className="text-[#FF6B35] text-xs">Service: {session.lead_data.service}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <div className="space-y-6">
            {/* Review Stats */}
            {reviewStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp size={20} className="text-[#FF6B35]" />
                    <span className="text-[#A0AAB2] text-sm">Total Requests</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{reviewStats.total_requests}</p>
                </div>
                <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Star size={20} className="text-green-400" />
                    <span className="text-[#A0AAB2] text-sm">Positive Reviews</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400">{reviewStats.positive_reviews}</p>
                </div>
                <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <ExternalLink size={20} className="text-blue-400" />
                    <span className="text-[#A0AAB2] text-sm">Sent to Google</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{reviewStats.redirected_to_google}</p>
                </div>
                <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertCircle size={20} className="text-yellow-400" />
                    <span className="text-[#A0AAB2] text-sm">Pending</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-400">{reviewStats.pending}</p>
                </div>
              </div>
            )}

            {/* Review Requests */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Review Requests ({reviewRequests.length})</h2>
              
              {reviewRequests.length === 0 ? (
                <p className="text-[#A0AAB2] text-center py-8">No review requests yet. Complete jobs to generate review requests.</p>
              ) : (
                <div className="space-y-4">
                  {reviewRequests.map((req) => (
                    <div key={req.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-white font-semibold">{req.customer_name}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              req.status === "completed" ? "bg-green-500/20 text-green-400" :
                              req.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                              "bg-yellow-500/20 text-yellow-400"
                            }`}>
                              {req.status}
                            </span>
                            {req.sentiment === "positive" && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">
                                Positive
                              </span>
                            )}
                            {req.sentiment === "negative" && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">
                                Needs Attention
                              </span>
                            )}
                          </div>
                          <p className="text-[#A0AAB2] text-sm">{req.service_completed}</p>
                          <p className="text-[#A0AAB2] text-xs">{req.customer_email} | {req.customer_phone}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {req.status === "pending" && (
                            <>
                              <button
                                onClick={() => copyReviewLink(req.id)}
                                className="px-3 py-1.5 bg-[#2A2B32] text-white rounded-lg text-sm hover:bg-[#3A3B42] transition-colors"
                              >
                                Copy Link
                              </button>
                              <button
                                onClick={() => handleSendReviewRequest(req.id)}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-[#FF6B35] text-white rounded-lg text-sm hover:bg-[#FF6B35]/90 transition-colors"
                              >
                                <Send size={14} />
                                <span>Mark Sent</span>
                              </button>
                            </>
                          )}
                          {req.redirected_to_google && (
                            <span className="text-green-400 text-sm flex items-center space-x-1">
                              <CheckCircle size={14} />
                              <span>Sent to Google</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Internal Feedback */}
            {internalFeedback.length > 0 && (
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <AlertCircle size={20} className="text-yellow-400" />
                  <span>Internal Feedback ({internalFeedback.length})</span>
                </h2>
                <div className="space-y-4">
                  {internalFeedback.map((fb) => (
                    <div key={fb.id} className="bg-[#1F2027] border border-yellow-500/30 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-white font-semibold">{fb.customer_name}</h3>
                          <p className="text-yellow-400 text-sm font-medium">{fb.issue}</p>
                          <p className="text-[#A0AAB2] text-sm mt-2">{fb.customer_notes}</p>
                        </div>
                        <span className="text-[#A0AAB2] text-xs">
                          {new Date(fb.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === "gallery" && (
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Upload New Image</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* File Upload */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#2A2B32] rounded-xl p-6 text-center cursor-pointer hover:border-[#FF6B35]/50 transition-colors"
                >
                  {previewUrl ? (
                    <div className="relative">
                      <img src={previewUrl} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewUrl(null);
                          setSelectedFile(null);
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload size={40} className="mx-auto text-[#A0AAB2] mb-2" />
                      <p className="text-[#A0AAB2]">Click to select an image</p>
                      <p className="text-[#A0AAB2] text-sm">JPG, PNG up to 5MB</p>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Title *</label>
                    <input
                      type="text"
                      value={uploadData.title}
                      onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                      placeholder="Project title"
                      data-testid="upload-title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Category</label>
                    <select
                      value={uploadData.category}
                      onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white focus:outline-none focus:border-[#FF6B35]"
                      data-testid="upload-category"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Description *</label>
                    <textarea
                      value={uploadData.description}
                      onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35] resize-none"
                      placeholder="Brief description"
                      data-testid="upload-description"
                    />
                  </div>
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !selectedFile || !uploadData.title || !uploadData.description}
                    className="w-full gradient-btn py-3 rounded-lg font-semibold disabled:opacity-50"
                    data-testid="upload-submit"
                  >
                    {uploading ? "Uploading..." : "Upload Image"}
                  </button>
                </div>
              </div>
            </div>

            {/* Gallery Grid */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Gallery Images ({galleryImages.length})</h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
                </div>
              ) : galleryImages.length === 0 ? (
                <p className="text-[#A0AAB2] text-center py-8">No images uploaded yet</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {galleryImages.map((image) => (
                    <div key={image.id} className="relative group rounded-lg overflow-hidden border border-[#2A2B32]">
                      <div className="aspect-square">
                        <img src={image.image_data} alt={image.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                        <button onClick={() => setEditingGallery(editingGallery === image.id ? null : image.id)}
                          className="p-2 bg-blue-500 rounded-full text-white hover:bg-blue-600 transition-colors" data-testid={`edit-image-${image.id}`}>
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDeleteImage(image.id)}
                          className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors" data-testid={`delete-image-${image.id}`}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="p-2 bg-[#1F2027]">
                        <p className="text-white text-xs font-medium truncate">{image.title}</p>
                        {image.description && <p className="text-[#A0AAB2] text-[10px] truncate">{image.description}</p>}
                      </div>
                      {/* Edit form */}
                      {editingGallery === image.id && (
                        <div className="absolute inset-0 bg-[#15161C] p-3 flex flex-col z-10">
                          <input type="text" defaultValue={image.title} id={`edit-title-${image.id}`}
                            className="w-full px-2 py-1.5 bg-[#1F2027] border border-[#2A2B32] rounded text-white text-xs mb-2"
                            placeholder="Title" />
                          <textarea defaultValue={image.description} id={`edit-desc-${image.id}`}
                            className="flex-1 w-full px-2 py-1.5 bg-[#1F2027] border border-[#2A2B32] rounded text-white text-xs resize-none mb-2"
                            placeholder="Description" />
                          <div className="flex space-x-2">
                            <button onClick={() => setEditingGallery(null)} className="flex-1 py-1.5 bg-[#2A2B32] text-white rounded text-xs">Cancel</button>
                            <button onClick={async () => {
                              const t = document.getElementById(`edit-title-${image.id}`).value;
                              const d = document.getElementById(`edit-desc-${image.id}`).value;
                              await axios.patch(`${API}/gallery/${image.id}`, { title: t, description: d }, { withCredentials: true });
                              toast.success("Updated!");
                              setEditingGallery(null);
                              fetchData();
                            }} className="flex-1 py-1.5 gradient-btn rounded text-xs font-semibold" data-testid={`save-edit-${image.id}`}>Save</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {/* Financing Leads Tab */}
        {activeTab === "financing-leads" && (
          <FinancingLeadsTab />
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Contact Submissions ({contacts.length})</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
              </div>
            ) : contacts.length === 0 ? (
              <p className="text-[#A0AAB2] text-center py-8">No contact submissions yet</p>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-1">{contact.name}</h3>
                        <p className="text-[#A0AAB2] text-sm mb-2">{contact.message}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-white text-sm">{contact.phone}</p>
                        <p className="text-[#A0AAB2] text-sm">{contact.email}</p>
                        <p className="text-[#A0AAB2] text-xs mt-1">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ghost Leads Tab */}
        {activeTab === "leads" && (() => {
          const filtered = ghostLeads.filter(lead => {
            if (leadFilter === "spam") return lead.is_spam;
            if (leadFilter === "new") return lead.status === "new";
            if (leadFilter === "contacted") return lead.status === "contacted";
            if (leadFilter === "uncontacted") return lead.status === "new" || !lead.status;
            return !lead.is_spam;
          });
          return (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-white">CRM Leads ({filtered.length})</h2>
              <div className="flex items-center space-x-2">
                {/* Filters */}
                {["all", "new", "uncontacted", "spam"].map(f => (
                  <button key={f} onClick={() => { setLeadFilter(f); setSelectedLeads([]); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${leadFilter === f ? "bg-[#FF6B35] text-white" : "bg-[#1F2027] text-[#A0AAB2] border border-[#2A2B32]"}`}
                    data-testid={`lead-filter-${f}`}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                {/* Bulk delete */}
                {selectedLeads.length > 0 && (
                  <button onClick={async () => {
                    if (!window.confirm(`Delete ${selectedLeads.length} leads?`)) return;
                    await axios.post(`${API}/leads/bulk-delete`, { ids: selectedLeads }, { withCredentials: true });
                    toast.success(`${selectedLeads.length} leads deleted`);
                    setSelectedLeads([]);
                    fetchData();
                  }} className="flex items-center space-x-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium" data-testid="bulk-delete-leads">
                    <Trash2 size={12} /><span>Delete ({selectedLeads.length})</span>
                  </button>
                )}
              </div>
            </div>
            
            {filtered.length === 0 ? (
              <p className="text-[#A0AAB2] text-center py-8">No leads found</p>
            ) : (
              <div className="space-y-3">
                {filtered.map((lead) => (
                  <div key={lead.id} className={`bg-[#1F2027] border rounded-lg p-4 ${lead.is_spam ? "border-red-500/30 opacity-60" : "border-[#2A2B32]"}`}>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" checked={selectedLeads.includes(lead.id)}
                        onChange={(e) => setSelectedLeads(prev => e.target.checked ? [...prev, lead.id] : prev.filter(id => id !== lead.id))}
                        className="mt-1.5 accent-[#FF6B35]" data-testid={`select-lead-${lead.id}`} />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-white font-semibold">{lead.name}</h3>
                          {lead.is_spam && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">SPAM</span>}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${lead.status === "new" ? "bg-blue-500/20 text-blue-400" : lead.status === "contacted" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}>{lead.status || "new"}</span>
                        </div>
                        <p className="text-[#FF6B35] text-sm">{lead.service}</p>
                        {lead.estimate_min > 0 && <p className="text-[#A0AAB2] text-xs">Est: ${lead.estimate_min?.toLocaleString()} - ${lead.estimate_max?.toLocaleString()}</p>}
                      </div>
                      <div className="flex items-center space-x-2">
                        <a href={`tel:${lead.phone}`} className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30" data-testid={`call-lead-${lead.id}`}>
                          <Phone size={14} />
                        </a>
                        <button onClick={async () => {
                          await axios.post(`${API}/ghost-leads/${lead.id}/import`, {}, { withCredentials: true });
                          toast.success("Imported to Sales CRM");
                          fetchData();
                        }} className="p-2 bg-[#FF6B35]/20 text-[#FF6B35] rounded-lg hover:bg-[#FF6B35]/30" title="Import to CRM">
                          <TrendingUp size={14} />
                        </button>
                        <button onClick={async () => {
                          await axios.patch(`${API}/leads/${lead.id}`, { is_spam: !lead.is_spam }, { withCredentials: true }).catch(() => {});
                          toast.success(lead.is_spam ? "Spam flag removed" : "Marked as spam");
                          fetchData();
                        }} className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30" title="Toggle spam">
                          <Ban size={14} />
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
                          await axios.delete(`${API}/leads/${lead.id}`, { withCredentials: true }).catch(() =>
                            axios.delete(`${API}/ghost-leads/${lead.id}`, { withCredentials: true })
                          );
                          toast.success("Lead deleted");
                          fetchData();
                        }} className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30" data-testid={`delete-lead-${lead.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 mt-2 ml-7">
                      <span className="text-white text-sm">{lead.phone}</span>
                      <span className="text-[#A0AAB2] text-xs">{new Date(lead.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })()}

        {/* Site Settings Tab */}
        {activeTab === "site-settings" && siteSettings && (
          <div className="space-y-6">
            {/* Social Media */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center space-x-2">
                <Globe size={18} className="text-[#FF6B35]" />
                <span>Social Media Links</span>
              </h3>
              <div className="space-y-4">
                {[
                  { key: "instagram_url", label: "Instagram URL", placeholder: "https://instagram.com/your_page" },
                  { key: "facebook_url", label: "Facebook URL", placeholder: "https://facebook.com/your_page" },
                  { key: "tiktok_url", label: "TikTok URL", placeholder: "https://tiktok.com/@your_page" },
                  { key: "display_username", label: "Display Username", placeholder: "@your_username" },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-sm text-[#A0AAB2] mb-1">{field.label}</label>
                    <input type="text" value={siteSettings[field.key] || ""}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2]/50 focus:outline-none focus:border-[#FF6B35]"
                      placeholder={field.placeholder} data-testid={`setting-${field.key}`} />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Google Review Link */}
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center space-x-2">
                <Star size={18} className="text-yellow-400" />
                <span>Google Review Link</span>
              </h3>
              <label className="block text-sm text-[#A0AAB2] mb-1">Google Business Review URL</label>
              <input type="text" value={siteSettings.google_review_link || ""}
                onChange={(e) => setSiteSettings(prev => ({ ...prev, google_review_link: e.target.value }))}
                className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2]/50 focus:outline-none focus:border-[#FF6B35]"
                placeholder="https://g.page/r/YOUR_PLACE_ID/review" data-testid="setting-google-review" />
              <p className="text-[#A0AAB2] text-xs mt-2">Positive reviewers will be redirected here to leave a public Google review</p>
            </div>

            <button onClick={async () => {
              const { key, ...data } = siteSettings;
              await axios.patch(`${API}/site-settings`, data, { withCredentials: true });
              toast.success("Settings saved!");
            }} className="gradient-btn px-6 py-3 rounded-xl font-semibold" data-testid="save-site-settings">
              Save All Settings
            </button>
          </div>
        )}

        {/* Public Reviews / Testimonials Tab */}
        {activeTab === "pub-reviews" && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Testimonials ({publicReviews.length})</h2>
              <button onClick={() => setShowAddReviewModal(true)} data-testid="add-review-btn"
                className="flex items-center space-x-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold">
                <Plus size={16} /><span>Add Review</span>
              </button>
            </div>
            {publicReviews.length === 0 ? (
              <p className="text-[#A0AAB2] text-center py-8">No reviews yet. Add one above or generate from completed jobs.</p>
            ) : (
              <div className="space-y-3">
                {publicReviews.map((review) => (
                  <div key={review.id} className={`bg-[#1F2027] border rounded-lg p-4 ${review.approved ? "border-green-500/30" : "border-[#2A2B32]"}`}
                    data-testid={`review-row-${review.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-white font-semibold">{review.name}</span>
                          <div className="flex text-yellow-400">
                            {[...Array(review.rating || 5)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                          </div>
                          {review.approved ? (
                            <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Live</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">Hidden</span>
                          )}
                        </div>
                        <p className="text-[#A0AAB2] text-sm">{review.message}</p>
                        {review.service && <p className="text-[#FF6B35] text-xs mt-1">{review.service}</p>}
                      </div>
                      <div className="flex space-x-2">
                        {!review.approved ? (
                          <button onClick={() => handleApproveReview(review.id)}
                            className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30" title="Approve"
                            data-testid={`approve-review-${review.id}`}>
                            <CheckCircle size={14} />
                          </button>
                        ) : (
                          <button onClick={() => handleUnapproveReview(review.id)}
                            className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30" title="Hide from public"
                            data-testid={`unapprove-review-${review.id}`}>
                            <XCircle size={14} />
                          </button>
                        )}
                        <button onClick={() => handleDeleteReview(review.id)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30" title="Delete"
                          data-testid={`delete-review-${review.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Client Profile Modal */}
      {selectedClientId && (
        <ClientProfileModal clientId={selectedClientId} onClose={() => setSelectedClientId(null)} />
      )}

      {/* Client Modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-white mb-4">Add New Client</h3>
            <div className="space-y-4">
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Name *</label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Email *</label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="client@email.com"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Phone *</label>
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Address</label>
                <input
                  type="text"
                  value={newClient.address}
                  onChange={(e) => setNewClient(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="123 Main St, Lakeland, FL"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Password * (for client login)</label>
                <input
                  type="text"
                  value={newClient.password}
                  onChange={(e) => setNewClient(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="Create a password"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowClientModal(false)}
                className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateClient}
                className="flex-1 py-3 gradient-btn rounded-lg font-semibold"
              >
                Create Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Review Modal */}
      {showAddReviewModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="add-review-modal">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Add Review</h3>
              <button onClick={() => setShowAddReviewModal(false)} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Customer Name *</label>
                <input type="text" value={newReview.name} onChange={e => setNewReview(p => ({...p, name: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="John Doe" data-testid="review-name" />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Rating</label>
                <div className="flex space-x-2">
                  {[1,2,3,4,5].map(r => (
                    <button key={r} onClick={() => setNewReview(p => ({...p, rating: r}))}
                      className={`p-2 rounded-lg ${newReview.rating >= r ? "text-yellow-400" : "text-[#2A2B32]"}`}>
                      <Star size={20} fill={newReview.rating >= r ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Review Text *</label>
                <textarea value={newReview.message} onChange={e => setNewReview(p => ({...p, message: e.target.value}))} rows={3}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white resize-none" placeholder="Great work..." data-testid="review-message" />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Service (optional)</label>
                <input type="text" value={newReview.service} onChange={e => setNewReview(p => ({...p, service: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Kitchen Renovation" data-testid="review-service" />
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" checked={newReview.approved} onChange={e => setNewReview(p => ({...p, approved: e.target.checked}))} id="review-approved" />
                <label htmlFor="review-approved" className="text-[#A0AAB2] text-sm">Approve immediately (show on homepage)</label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button onClick={() => setShowAddReviewModal(false)} className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg">Cancel</button>
              <button onClick={handleCreateReview} className="flex-1 py-3 gradient-btn rounded-lg font-semibold" data-testid="submit-review-btn">Add Review</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
